import { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Rocket,
  Code2,
  Database,
  Server,
  CheckCircle,
  Star,
  Zap,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Twitter,
  Linkedin,
  Youtube,
  MessageCircle,
  Layout,
  GitBranch,
  Cloud,
  Layers,
  Boxes,
  Cpu,
  Globe,
  Clock,
  Users,
} from 'lucide-react';
import { useThemeStore } from '../../../store/theme';

// CSS Keyframe animations
const keyframeStyles = `
  @keyframes float-gentle {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-15px) rotate(3deg); }
  }

  @keyframes pulse-violet {
    0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.4); }
    50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.7); }
  }

  @keyframes orbit-mvp {
    from { transform: rotate(0deg) translateX(120px) rotate(0deg); }
    to { transform: rotate(360deg) translateX(120px) rotate(-360deg); }
  }

  @keyframes orbit-mvp-reverse {
    from { transform: rotate(360deg) translateX(100px) rotate(-360deg); }
    to { transform: rotate(0deg) translateX(100px) rotate(0deg); }
  }

  @keyframes ripple {
    0% { transform: scale(0.8); opacity: 1; }
    100% { transform: scale(2); opacity: 0; }
  }

  @keyframes rocket-float {
    0%, 100% { transform: translateY(0) rotate(-5deg); opacity: 0.6; }
    50% { transform: translateY(-20px) rotate(5deg); opacity: 1; }
  }

  @keyframes launch-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }

  .animate-float-gentle { animation: float-gentle 6s ease-in-out infinite; }
  .animate-pulse-violet { animation: pulse-violet 3s ease-in-out infinite; }
  .animate-orbit-mvp { animation: orbit-mvp 20s linear infinite; }
  .animate-orbit-mvp-reverse { animation: orbit-mvp-reverse 15s linear infinite; }
  .animate-ripple { animation: ripple 2s ease-out infinite; }
  .animate-rocket-float { animation: rocket-float 4s ease-in-out infinite; }
  .animate-launch-pulse { animation: launch-pulse 2s ease-in-out infinite; }
`;

// Helper function to render markdown-style bold text
const renderMessageContent = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// Chat demo messages
const chatMessages = [
  { role: 'user', message: "I have an idea for a task management app for remote teams. Can you help me build an MVP?" },
  { role: 'assistant', message: "Exciting idea! I'd love to help you build this MVP. Let me understand your vision better:\n\n**Core Questions:**\n1. What's the key differentiator from existing tools like Asana/Trello?\n2. Who's your target user - small teams, enterprises, or freelancers?\n3. What are the 3-5 must-have features for launch?\n4. Do you have a preferred tech stack or should I recommend one?\n5. What's your timeline for the MVP?\n\nOnce I understand these, I'll propose an architecture and we can start building!" },
  { role: 'user', message: "It's focused on async communication with AI summaries of discussions. Target: remote startups (5-20 people). Must-haves: task boards, async video updates, AI daily digests. No preference on stack. 4-6 weeks timeline." },
  { role: 'assistant', message: "Great vision! Here's my recommended MVP architecture:\n\n**Tech Stack:**\n- Frontend: Next.js 14 + Tailwind CSS + Framer Motion\n- Backend: Node.js + Express + PostgreSQL\n- AI: OpenAI API for summaries\n- Video: Mux for async video\n- Auth: Clerk or NextAuth\n- Deploy: Vercel + Railway\n\n**MVP Feature Breakdown:**\n\n**Week 1-2: Foundation**\n- User auth & team workspaces\n- Database schema design\n- Basic task board (Kanban style)\n\n**Week 3-4: Core Features**\n- Async video recording & playback\n- Task comments & discussions\n- Basic notification system\n\n**Week 5-6: AI & Polish**\n- AI daily digest generation\n- Discussion summarization\n- Mobile-responsive UI\n- Beta deployment\n\n**Database Schema Preview:**\n```sql\nusers, teams, team_members,\nprojects, tasks, comments,\nvideo_updates, ai_summaries\n```\n\nShall I start with the database schema and authentication setup?" },
];

// Testimonials
const testimonials = [
  {
    name: 'Alex Rivera',
    role: 'Technical Founder',
    company: 'LaunchFast',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    quote: "MVP Maker helped me go from idea to a working prototype in 3 weeks. The architecture guidance alone saved me months of refactoring later. It's like having a senior CTO on demand.",
    rating: 5,
  },
  {
    name: 'Priya Sharma',
    role: 'Product Manager',
    company: 'InnovateTech',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=face',
    quote: "We used MVP Maker to validate our product hypothesis before committing engineering resources. The prototype was good enough to show investors and get our seed round.",
    rating: 5,
  },
  {
    name: 'Jordan Chen',
    role: 'Solo Entrepreneur',
    company: 'SideProject.io',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    quote: "As a non-technical founder, MVP Maker was a game-changer. It explained complex concepts in simple terms and helped me build something I could actually ship.",
    rating: 5,
  },
];

// Capabilities with unique images
const capabilities = [
  {
    icon: Boxes,
    title: 'Full-Stack Prototyping',
    description: 'Build complete working prototypes with frontend, backend, and database - all integrated and ready for users.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&h=400&fit=crop',
  },
  {
    icon: Layers,
    title: 'Architecture Design',
    description: 'Get scalable architecture recommendations based on your specific needs, traffic expectations, and team size.',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop',
  },
  {
    icon: Database,
    title: 'Database Schema',
    description: 'Design optimized database structures with proper relationships, indexes, and migration strategies.',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop',
  },
  {
    icon: Server,
    title: 'API Development',
    description: 'Build RESTful or GraphQL APIs with authentication, rate limiting, and comprehensive documentation.',
    image: 'https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=600&h=400&fit=crop',
  },
  {
    icon: Layout,
    title: 'Frontend UI/UX',
    description: 'Create beautiful, responsive user interfaces with modern frameworks and best practices.',
    image: 'https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?w=600&h=400&fit=crop',
  },
  {
    icon: Cloud,
    title: 'Deployment Setup',
    description: 'Configure hosting, CI/CD pipelines, monitoring, and everything needed to go live.',
    image: 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=600&h=400&fit=crop',
  },
];

// Discover more tasks
const discoverTasks = [
  {
    title: 'Build My MVP',
    description: 'Transform your idea into a working prototype with full-stack implementation guidance and code generation.',
    gif: 'https://media.giphy.com/media/3oKIPtjElfqwMOTbH2/giphy.gif',
    prompt: 'Help me build an MVP for my startup idea',
  },
  {
    title: 'Design Architecture',
    description: 'Get a scalable system architecture tailored to your specific requirements and growth plans.',
    gif: 'https://media.giphy.com/media/l46Cy1rHbQ92uuLXa/giphy.gif',
    prompt: 'Help me design the architecture for my application',
  },
  {
    title: 'Create Database Schema',
    description: 'Design an efficient database structure with proper relationships and optimized for your use cases.',
    gif: 'https://media.giphy.com/media/3oKIPnAiaMCws8nOsE/giphy.gif',
    prompt: 'Help me design the database schema for my app',
  },
  {
    title: 'Choose Tech Stack',
    description: 'Get recommendations on the best technologies for your project based on requirements and constraints.',
    gif: 'https://media.giphy.com/media/xT9C25UNTwfZuk85WP/giphy.gif',
    prompt: 'Help me choose the right tech stack for my project',
  },
  {
    title: 'Build API Endpoints',
    description: 'Design and implement RESTful or GraphQL APIs with authentication and proper error handling.',
    gif: 'https://media.giphy.com/media/LmNwrBhejkK9EFP504/giphy.gif',
    prompt: 'Help me build the API for my application',
  },
  {
    title: 'Setup Deployment',
    description: 'Configure cloud hosting, CI/CD pipelines, and monitoring for your application.',
    gif: 'https://media.giphy.com/media/3oKIPEqDGUULpEU0aQ/giphy.gif',
    prompt: 'Help me set up deployment and DevOps for my project',
  },
];

// Related employees
const relatedEmployees = [
  { id: 'code', name: 'Code', role: 'Developer Assistant', avatar: '💻', gradient: 'from-slate-500 to-gray-500' },
  { id: 'atlas', name: 'Atlas', role: 'Research Specialist', avatar: '🔬', gradient: 'from-indigo-500 to-purple-500' },
  { id: 'sage', name: 'Sage', role: 'Business Strategist', avatar: '🦉', gradient: 'from-emerald-500 to-teal-500' },
];

// Stats
const stats = [
  { label: 'MVPs Launched', value: '10K+', icon: Rocket },
  { label: 'Founder Satisfaction', value: '4.9/5', icon: Star },
  { label: 'Lines of Code', value: '50M+', icon: Code2 },
  { label: 'Avg Build Time', value: '3 weeks', icon: Clock },
];

export default function MvpMakerEmployee() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState(0);
  const chatRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(chatRef, { once: true, margin: '-100px' });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate chat messages when in view
  useEffect(() => {
    if (isInView && visibleMessages < chatMessages.length) {
      const timeout = setTimeout(() => {
        setVisibleMessages((prev) => prev + 1);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isInView, visibleMessages]);

  // JSON-LD Schema
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'MVP Maker - AI Prototype Builder',
    applicationCategory: 'DeveloperApplication',
    description: 'AI-powered prototype builder that transforms ideas into fully functional MVPs. From concept to production-ready code in one conversation.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '7823',
    },
    operatingSystem: 'Web-based',
    featureList: [
      'Full-stack prototyping',
      'Architecture design',
      'Database schema',
      'API development',
      'Frontend UI/UX',
      'Deployment setup',
      'Tech stack recommendations',
      'Code generation',
    ],
  };

  return (
    <>
      <Helmet>
        <title>MVP Maker - AI Prototype Builder | SweetPilot</title>
        <meta
          name="description"
          content="Meet MVP Maker, your AI prototype builder. Transform ideas into fully functional MVPs with full-stack development, architecture design, and deployment setup."
        />
        <meta
          name="keywords"
          content="AI MVP builder, prototype builder, full-stack development, startup MVP, rapid prototyping, code generation, architecture design, API development"
        />
        <link rel="canonical" href="https://sweetpilot.com/employees/mvp" />

        {/* Open Graph */}
        <meta property="og:title" content="MVP Maker - AI Prototype Builder | SweetPilot" />
        <meta
          property="og:description"
          content="Transform your ideas into production-ready MVPs. AI-powered full-stack prototyping."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sweetpilot.com/employees/mvp" />
        <meta property="og:image" content="https://sweetpilot.com/og-mvp.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="MVP Maker - AI Prototype Builder" />
        <meta
          name="twitter:description"
          content="Transform your ideas into production-ready MVPs. AI-powered full-stack prototyping."
        />
        <meta name="twitter:image" content="https://sweetpilot.com/og-mvp.png" />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <style>{keyframeStyles}</style>

      <div className={`min-h-screen ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        {/* Floating Rocket Background */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-rocket-float"
              style={{
                left: `${10 + i * 12}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${4 + i * 0.5}s`,
              }}
            >
              <Rocket
                className={`w-6 h-6 ${isDark ? 'text-violet-500/20' : 'text-violet-400/30'}`}
              />
            </div>
          ))}
        </div>

        {/* Navigation Header */}
        <nav className={`fixed top-0 left-0 right-0 z-50 ${isDark ? 'bg-dark-900/80' : 'bg-white/80'} backdrop-blur-xl border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/employees" className={`flex items-center gap-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Employees</span>
              </Link>
              <Link
                to="/dashboard/employees/mvp"
                className="px-4 py-2 bg-gradient-to-r from-violet-500 to-indigo-500 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-violet-500/25 transition-all"
              >
                Chat with MVP Maker
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-transparent to-indigo-500/10" />

          {/* Animated ripples */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-96 h-96 rounded-full border-2 border-violet-500/20 animate-ripple" />
            <div className="absolute inset-0 w-96 h-96 rounded-full border-2 border-violet-500/20 animate-ripple" style={{ animationDelay: '0.5s' }} />
            <div className="absolute inset-0 w-96 h-96 rounded-full border-2 border-violet-500/20 animate-ripple" style={{ animationDelay: '1s' }} />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Breadcrumbs */}
            <nav className="mb-8">
              <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                <li>
                  <Link
                    to="/employees"
                    className={`transition-colors ${isDark ? 'hover:text-violet-400' : 'hover:text-violet-600'}`}
                  >
                    AI Employees
                  </Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li className={isDark ? 'text-white' : 'text-slate-900'}>
                  MVP Maker
                </li>
              </ol>
            </nav>

            <div className="text-center">
              {/* Avatar with orbiting icons */}
              <div className="relative w-48 h-48 mx-auto mb-8">
                {/* Orbiting icons */}
                <div className="absolute inset-0 animate-orbit-mvp">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                      <Code2 className="w-6 h-6 text-violet-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-mvp-reverse">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                      <Database className="w-6 h-6 text-indigo-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-mvp" style={{ animationDelay: '-10s' }}>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                      <Cloud className="w-6 h-6 text-purple-500" />
                    </div>
                  </div>
                </div>

                {/* Main avatar */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.8 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div className="w-36 h-36 rounded-3xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center animate-float-gentle animate-pulse-violet">
                    <span className="text-7xl">🚀</span>
                  </div>
                </motion.div>
              </div>

              {/* Title */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className={`text-5xl md:text-7xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Meet{' '}
                  <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
                    MVP Maker
                  </span>
                </h1>
                <p className={`text-xl md:text-2xl mb-2 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                  Your AI Prototype Builder
                </p>
                <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                  Transform your ideas into fully functional prototypes. From concept to production-ready MVP in one conversation.
                </p>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap justify-center gap-4 mt-8"
              >
                <Link
                  to="/dashboard/employees/mvp"
                  className="group px-8 py-4 bg-gradient-to-r from-violet-500 to-indigo-500 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-violet-500/30 transition-all flex items-center gap-2"
                >
                  Start Building Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="#capabilities"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-violet-500/50 text-violet-400 hover:bg-violet-500/10'
                      : 'border-violet-500 text-violet-600 hover:bg-violet-50'
                  }`}
                >
                  Explore Features
                </a>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
              >
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-white shadow-sm'}`}
                  >
                    <stat.icon className={`w-6 h-6 mx-auto mb-2 ${isDark ? 'text-violet-400' : 'text-violet-500'}`} />
                    <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {stat.value}
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Chat Demo Section */}
        <section ref={chatRef} className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                See MVP Maker in Action
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Watch how MVP Maker turns ideas into reality
              </p>
            </motion.div>

            {/* Chat Window */}
            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-dark-900 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              {/* Chat Header */}
              <div className={`px-6 py-4 border-b ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                    <span className="text-lg">🚀</span>
                  </div>
                  <div>
                    <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>MVP Maker</div>
                    <div className="flex items-center gap-1 text-sm text-violet-500">
                      <span className="w-2 h-2 bg-violet-500 rounded-full animate-pulse" />
                      Online - Prototype Builder
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="p-6 space-y-4 min-h-[400px]">
                <AnimatePresence>
                  {chatMessages.slice(0, visibleMessages).map((msg, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-4 rounded-2xl whitespace-pre-line ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white'
                            : isDark
                            ? 'bg-dark-700 text-gray-200'
                            : 'bg-white text-slate-700 shadow-sm'
                        }`}
                      >
                        <span className="whitespace-pre-line">{renderMessageContent(msg.message)}</span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Typing indicator */}
                {isInView && visibleMessages < chatMessages.length && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className={`px-4 py-3 rounded-2xl ${isDark ? 'bg-dark-700' : 'bg-white shadow-sm'}`}>
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section id="capabilities" className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Everything You Need to{' '}
                <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
                  Launch Fast
                </span>
              </h2>
              <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                MVP Maker comes equipped with powerful capabilities to take you from idea to launch
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`group rounded-2xl overflow-hidden border ${
                    isDark ? 'bg-dark-800/50 border-white/5 hover:border-violet-500/30' : 'bg-white border-slate-200 hover:border-violet-300'
                  } transition-all hover:shadow-xl hover:shadow-violet-500/10`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={capability.image}
                      alt={capability.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="p-2 rounded-xl bg-gradient-to-r from-violet-500 to-indigo-500">
                        <capability.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {capability.title}
                    </h3>
                    <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                      {capability.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Tech Stack Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Modern Tech Stack Expertise
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                MVP Maker works with the latest technologies and best practices
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-6">
              {[
                { icon: Layout, label: 'Frontend', color: 'from-violet-500 to-indigo-500' },
                { icon: Server, label: 'Backend', color: 'from-indigo-500 to-violet-500' },
                { icon: Database, label: 'Databases', color: 'from-violet-600 to-indigo-600' },
                { icon: Cloud, label: 'Cloud/DevOps', color: 'from-indigo-600 to-violet-600' },
                { icon: GitBranch, label: 'Version Control', color: 'from-violet-500 to-purple-500' },
              ].map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex flex-col items-center gap-3 p-6 rounded-2xl ${
                    isDark ? 'bg-dark-900/50' : 'bg-slate-50'
                  }`}
                >
                  <div className={`p-4 rounded-2xl bg-gradient-to-r ${tech.color}`}>
                    <tech.icon className="w-8 h-8 text-white" />
                  </div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {tech.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Discover More Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Discover What MVP Maker Can Do
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Click any task to try it with MVP Maker
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {discoverTasks.map((task, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/dashboard/employees/mvp?prompt=${encodeURIComponent(task.prompt)}`}
                    className={`group relative block min-h-72 rounded-2xl overflow-hidden border ${
                      isDark ? 'border-white/10 hover:border-violet-500/50' : 'border-slate-200 hover:border-violet-300'
                    } transition-all`}
                  >
                    {/* Background Image/GIF */}
                    <div className="absolute inset-0">
                      <img
                        src={task.gif}
                        alt={task.title}
                        className="w-full h-full object-cover opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                      />
                      <div className={`absolute inset-0 ${isDark ? 'bg-dark-800' : 'bg-white'} group-hover:opacity-80 transition-opacity`} />
                    </div>

                    {/* Content */}
                    <div className="relative h-full p-6 flex flex-col justify-end">
                      <div className="mb-auto">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-indigo-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <Rocket className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {task.title}
                      </h3>
                      <p className={`line-clamp-3 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {task.description}
                      </p>
                      <div className={`mt-4 flex items-center gap-2 text-violet-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity`}>
                        <span>Try this now</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Loved by Founders
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                See how MVP Maker is helping founders build faster
              </p>
            </motion.div>

            {/* Testimonial Carousel */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className={`p-8 rounded-2xl ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-4 justify-center">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className={`text-xl text-center mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    "{testimonials[currentTestimonial].quote}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center justify-center gap-4">
                    <img
                      src={testimonials[currentTestimonial].avatar}
                      alt={testimonials[currentTestimonial].name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="text-left">
                      <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {testimonials[currentTestimonial].name}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {testimonials[currentTestimonial].role} at {testimonials[currentTestimonial].company}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentTestimonial
                        ? 'w-8 bg-gradient-to-r from-violet-500 to-indigo-500'
                        : isDark
                        ? 'bg-white/20 hover:bg-white/40'
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Employees */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Meet More AI Employees
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Build your complete AI team
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {relatedEmployees.map((employee, index) => (
                <motion.div
                  key={employee.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/employees/${employee.id}`}
                    className={`group block p-6 rounded-2xl border transition-all ${
                      isDark
                        ? 'bg-dark-800/50 border-white/5 hover:border-white/20'
                        : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <span className="text-3xl">{employee.avatar}</span>
                    </div>
                    <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {employee.name}
                    </h3>
                    <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {employee.role}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={`py-20 ${isDark ? 'bg-gradient-to-br from-violet-500/10 via-dark-900 to-indigo-500/10' : 'bg-gradient-to-br from-violet-50 via-white to-indigo-50'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center animate-float-gentle">
                <span className="text-5xl">🚀</span>
              </div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Ready to Launch Your MVP?
              </h2>
              <p className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Start building your idea today with MVP Maker
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/dashboard/employees/mvp"
                  className="group px-8 py-4 bg-gradient-to-r from-violet-500 to-indigo-500 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-violet-500/30 transition-all flex items-center gap-2"
                >
                  Chat with MVP Maker Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/employees"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-white/20 text-white hover:bg-white/5'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  View All Employees
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className={`py-16 border-t ${isDark ? 'border-white/5 bg-dark-900' : 'border-slate-200 bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
              {/* Logo */}
              <div className="col-span-2">
                <Link to="/" className="inline-block mb-4">
                  <img
                    src={isDark ? '/logos/logo-5.png' : '/logos/logo-6.png'}
                    alt="SweetPilot"
                    className="w-48 object-contain"
                  />
                </Link>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                  AI employees that never sleep. Build, grow, and scale your
                  business 24/7.
                </p>
                <div className="flex space-x-3">
                  {[
                    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/sweetpilot' },
                    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/sweetpilot' },
                    { name: 'Discord', icon: MessageCircle, href: 'https://discord.gg/sweetpilot' },
                    { name: 'YouTube', icon: Youtube, href: 'https://youtube.com/@sweetpilot' },
                  ].map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                        isDark
                          ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                          : 'bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200'
                      }`}
                    >
                      <span className="sr-only">{social.name}</span>
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Links */}
              {[
                {
                  title: 'Product',
                  links: [
                    { name: 'AI Employees', href: '/employees' },
                    { name: 'Features', href: '/features' },
                    { name: 'Pricing', href: '/pricing' },
                    { name: 'Integrations', href: '/integrations' },
                    { name: 'API', href: '/api' },
                  ],
                },
                {
                  title: 'Company',
                  links: [
                    { name: 'About', href: '/about' },
                    { name: 'Blog', href: '/blog' },
                    { name: 'Careers', href: '/careers' },
                    { name: 'Press', href: '/press' },
                    { name: 'Contact', href: '/contact' },
                  ],
                },
                {
                  title: 'Resources',
                  links: [
                    { name: 'Documentation', href: '/docs' },
                    { name: 'Help Center', href: '/help' },
                    { name: 'Community', href: '/community' },
                    { name: 'Templates', href: '/templates' },
                    { name: 'Roadmap', href: '/roadmap' },
                    { name: 'Status', href: '/status' },
                  ],
                },
                {
                  title: 'Legal',
                  links: [
                    { name: 'Privacy', href: '/privacy' },
                    { name: 'Terms', href: '/terms' },
                    { name: 'Security', href: '/security' },
                    { name: 'Cookies', href: '/cookies' },
                    { name: 'GDPR', href: '/gdpr' },
                    { name: 'Refund Policy', href: '/refund-policy' },
                  ],
                },
              ].map((column) => (
                <div key={column.title}>
                  <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{column.title}</h4>
                  <ul className="space-y-3">
                    {column.links.map((link) => (
                      <li key={link.name}>
                        <Link
                          to={link.href}
                          className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className={`flex flex-col md:flex-row items-center justify-between pt-8 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
              <p className={`text-sm mb-4 md:mb-0 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                &copy; {new Date().getFullYear()} SweetPilot. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <Link to="/privacy" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Privacy Policy
                </Link>
                <Link to="/terms" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Terms of Service
                </Link>
                <Link to="/cookies" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Cookies
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
